import React, { useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// Inline company picker — searches the knowledge map (companies) by name/ticker and calls
// onPick with the chosen entity. Deterministic contains/iexact; no fuzzy scoring.
function CompanyPicker({ onPick, onCancel }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  useEffect(() => {
    const t = (q || '').trim();
    if (t.length < 2) { setRes([]); return undefined; }
    let live = true;
    const h = setTimeout(() => {
      researchosService.searchEntities(t).then((r) => { if (live) setRes(r.data || []); }).catch(() => {});
    }, 200);
    return () => { live = false; clearTimeout(h); };
  }, [q]);
  return (
    <span className="relative inline-flex items-center gap-1">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="company name / ticker"
        className="w-44 rounded border border-gray-200 bg-transparent px-2 py-0.5 text-xs text-gray-700 outline-none focus:border-brand-gold dark:border-gray-700 dark:text-gray-200" />
      <button onClick={onCancel} className="text-gray-400 hover:text-red-500 text-xs">✕</button>
      {res.length > 0 && (
        <div className="absolute right-0 top-6 z-30 max-h-56 w-64 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-[#161b22]">
          {res.map((e) => (
            <button key={e.key} onClick={() => onPick(e)}
              className="block w-full px-2 py-1 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-800">
              <span className="font-medium text-gray-900 dark:text-gray-100">{e.ticker || e.name}</span>
              {e.ticker && <span className="ml-1 text-gray-500 dark:text-gray-400">{e.name}</span>}
              {!e.tradeable && <span className="ml-1 text-[10px] uppercase text-gray-400">private</span>}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// Official-channel declaration control for ONE channel (superuser). Shows the declared company
// (with a clear ✕) or a "＋ official channel of…" button that opens the company picker. Writes
// via setOfficialChannel and reports the new declaration up through onChange. `declared` = the
// current {key,ticker,name} or null; `channelId` must be the immutable channel_id.
export default function OfficialChannelControl({ channelId, declared, suggestion, onChange }) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!channelId) {
    return <span className="text-xs text-gray-300 dark:text-gray-600" title="No immutable channel_id — re-run to stamp it">Official —</span>;
  }
  const write = (entity) => {
    setBusy(true);
    researchosService.setOfficialChannel({ channel_id: channelId, entity_key: entity ? entity.key : '' })
      .then((r) => { onChange(r.data?.official_of || null); setPicking(false); })
      .finally(() => setBusy(false));
  };
  if (declared) {
    return (
      <span className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-brand-gold" style={{ border: '1px solid rgb(var(--brand-gold-rgb))' }}>
        ★ Official: {declared.ticker || declared.name}
        <button disabled={busy} onClick={() => write(null)} className="ml-0.5 text-gray-400 hover:text-red-500" title="Clear declaration">✕</button>
      </span>
    );
  }
  // Exact-name auto-match — NOT applied to scoring until you confirm it (declared-only rule).
  if (suggestion && !picking) {
    return (
      <span className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs" style={{ border: '1px dashed #d1d5db' }}
        title="This channel's name matches this company — confirm to make it official (not applied until you do)">
        <span className="text-gray-500 dark:text-gray-400">Suggested: {suggestion.ticker || suggestion.name}</span>
        <button disabled={busy} onClick={() => write(suggestion)} className="font-medium text-brand-gold hover:underline">Confirm</button>
        <span className="text-gray-300">·</span>
        <button onClick={() => setPicking(true)} className="text-gray-400 hover:text-gray-600" title="Pick a different company">change</button>
      </span>
    );
  }
  return picking
    ? <CompanyPicker onPick={write} onCancel={() => setPicking(false)} />
    : (
      <button onClick={() => setPicking(true)}
        className="rounded px-2.5 py-1 text-xs text-gray-600 hover:bg-brand-gold/10 hover:text-brand-gold dark:text-gray-300"
        style={{ border: '1px solid #d1d5db' }}
        title="Declare this channel the official channel of a company">
        Set official company
      </button>
    );
}
